import React, { useState } from 'react'
import { Spinner } from '@fluentui/react'
import {
  EquipmentType,
  errorToString,
  solarTradelineItemRequirements,
  SOLAR_TRADELINE_LOAN_AGREEMENT_DOC_TYPE,
  SOLAR_TRADELINE_PAYOFF_LETTER_DOC_TYPE,
  type SolarDoc,
  type SolarTradelineInquiryItem,
  type SolarTradelineUploadedDoc,
} from '@oneethos/shared'
import { MultiFileUpload } from '../../components/forms/multi-file-upload'
import { uploadFile } from '../../services/fileUploadService'
import type { ConsumerAppFormData, SubformProps } from '.'

type SolarTradelineInquiryHoldProps = Pick<
  SubformProps,
  'formData' | 'update' | 'save' | 'onError' | 'saving'
>

type InquiryItemPatch = Partial<Pick<
  SolarTradelineInquiryItem,
  'isSolarLoan' | 'sameProperty' | 'paidOff' | 'loanAgreementDocs' | 'payoffLetterDocs'
>>

const partyLabel = (
  party: SolarTradelineInquiryItem['party'],
  formData: Pick<ConsumerAppFormData, 'customerName' | 'coFirstName' | 'coLastName'>
): string => {
  if (party === 'coborrower') {
    return [formData.coFirstName, formData.coLastName].filter(Boolean).join(' ')
  }

  return formData.customerName
}

const applyLocalInquiryUpdate = (
  item: SolarTradelineInquiryItem,
  patch: InquiryItemPatch
): SolarTradelineInquiryItem => {
  if ('isSolarLoan' in patch && patch.isSolarLoan !== item.isSolarLoan) {
    return {
      ...item,
      isSolarLoan: patch.isSolarLoan,
      sameProperty: undefined,
      paidOff: undefined,
      storageOnly: undefined,
      loanAgreementDocs: [],
      payoffLetterDocs: [],
      complete: false,
    }
  }

  if ('sameProperty' in patch && patch.sameProperty !== item.sameProperty) {
    return {
      ...item,
      sameProperty: patch.sameProperty,
      paidOff: undefined,
      storageOnly: undefined,
      payoffLetterDocs: [],
      loanAgreementDocs: patch.sameProperty === false ? item.loanAgreementDocs : [],
      complete: false,
    }
  }

  if ('paidOff' in patch && patch.paidOff !== item.paidOff) {
    return {
      ...item,
      paidOff: patch.paidOff,
      storageOnly: undefined,
      payoffLetterDocs: [],
      complete: false,
    }
  }

  return {
    ...item,
    ...patch,
    complete: false,
  }
}

const SolarTradelineYesNo = ({
  id,
  label,
  value,
  onChange,
}: {
  id: string
  label: string
  value: boolean | undefined
  onChange: (value: boolean | undefined) => void
}) => {
  const selected = value === true ? 'yes' : value === false ? 'no' : ''

  return (
    <div className="form-group">
      <label className="form-label" htmlFor={id}>{label}</label>
      <select
        id={id}
        className="form-select"
        value={selected}
        onChange={ev => {
          if (ev.target.value === 'yes') onChange(true)
          else if (ev.target.value === 'no') onChange(false)
          else onChange(undefined)
        }}
      >
        <option value="">Select</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </div>
  )
}

const SolarTradelineDocUpload = ({
  id,
  label,
  description,
  docs,
  onChange,
}: {
  id: string
  label: string
  description: string
  docs: SolarTradelineUploadedDoc[] | undefined
  onChange: (docs: SolarTradelineInquiryItem['loanAgreementDocs']) => void
}) => {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <p>{description}</p>
      <MultiFileUpload
        id={id}
        docs={(docs ?? []) as (File & SolarDoc)[]}
        onChange={files => onChange(files as unknown as SolarTradelineUploadedDoc[])}
      />
    </div>
  )
}

const SolarTradelineInquiryItemForm = ({
  item,
  formData,
  storageOnlyProject,
  onChange,
}: {
  item: SolarTradelineInquiryItem
  formData: Pick<ConsumerAppFormData, 'customerName' | 'coFirstName' | 'coLastName'>
  storageOnlyProject: boolean
  onChange: (patch: InquiryItemPatch) => void
}) => {
  const requirements = solarTradelineItemRequirements(item, storageOnlyProject)

  return (
    <fieldset className="mb-4">
      <legend className="h5">
        {partyLabel(item.party, formData)}: {item.customerName || item.matchedLenderName}
      </legend>
      <SolarTradelineYesNo
        id={`${item.id}-is-solar-loan`}
        label="Is this an existing solar loan?"
        value={item.isSolarLoan}
        onChange={isSolarLoan => onChange({ isSolarLoan })}
      />
      {item.isSolarLoan === true ? (
        <SolarTradelineYesNo
          id={`${item.id}-same-property`}
          label="Is this solar loan for the same property as this project?"
          value={item.sameProperty}
          onChange={sameProperty => onChange({ sameProperty })}
        />
      ) : null}
      {item.isSolarLoan === true && item.sameProperty === true ? (
        <SolarTradelineYesNo
          id={`${item.id}-paid-off`}
          label="Has this solar loan been paid off?"
          value={item.paidOff}
          onChange={paidOff => onChange({ paidOff })}
        />
      ) : null}
      {requirements.needsAgreement ? (
        <SolarTradelineDocUpload
          id={`${item.id}-agreement`}
          label="Loan agreement"
          description="Upload the loan agreement for this account."
          docs={item.loanAgreementDocs}
          onChange={loanAgreementDocs => onChange({ loanAgreementDocs })}
        />
      ) : null}
      {requirements.needsPayoff ? (
        <SolarTradelineDocUpload
          id={`${item.id}-payoff`}
          label="Payoff letter"
          description="Upload the payoff letter for this account."
          docs={item.payoffLetterDocs}
          onChange={payoffLetterDocs => onChange({ payoffLetterDocs })}
        />
      ) : null}
      {requirements.shouldDecline && null}
    </fieldset>
  )
}

const itemReadyToSubmit = (
  item: SolarTradelineInquiryItem,
  storageOnlyProject: boolean
): boolean => {
  const requirements = solarTradelineItemRequirements(item, storageOnlyProject)
  if (requirements.awaitingAnswer || requirements.shouldDecline) return false
  if (requirements.needsAgreement && !(item.loanAgreementDocs?.length)) return false
  if (requirements.needsPayoff && !(item.payoffLetterDocs?.length)) return false
  return true
}

const uploadInquiryDocs = async (
  docs: SolarTradelineUploadedDoc[] | undefined,
  docType: string
): Promise<SolarTradelineUploadedDoc[]> => {
  if (!docs?.length) return []

  return Promise.all(docs.map(async (doc) => {
    if (doc.containerName && doc.blobName) {
      return {
        containerName: doc.containerName,
        blobName: doc.blobName,
        label: doc.label,
        contentType: doc.contentType,
        docType,
      }
    }

    const file = doc as unknown as File
    const uploaded = await uploadFile(file)
    return {
      ...uploaded,
      label: file.name || uploaded.label,
      contentType: file.type || uploaded.contentType,
      docType,
    }
  }))
}

export const SolarTradelineInquiryHold = ({
  formData,
  update,
  save,
  onError,
  saving,
}: SolarTradelineInquiryHoldProps) => {
  const [submitting, setSubmitting] = useState(false)
  const items = formData.solarTradelineInquiryItems ?? []
  const storageOnlyProject = formData.equipmentType === EquipmentType.StorageOnly
  const requirementsByItem = items.map(item => ({
    item,
    requirements: solarTradelineItemRequirements(item, storageOnlyProject),
  }))
  const declining = requirementsByItem.some(({ requirements }) => requirements.shouldDecline)
  const visibleItems = declining
    ? requirementsByItem
      .filter(({ requirements }) => !requirements.awaitingAnswer)
      .map(({ item }) => item)
    : items
  const canSubmit = items.length > 0 && (
    declining || items.every(item => itemReadyToSubmit(item, storageOnlyProject))
  )

  const updateItem = (id: string, patch: InquiryItemPatch) => {
    update({
      ...formData,
      solarTradelineInquiryItems: items.map(item => (
        item.id === id ? applyLocalInquiryUpdate(item, patch) : item
      )),
    })
  }

  const handleContinue = async () => {
    try {
      setSubmitting(true)
      const uploadedItems = await Promise.all(items.map(async (item) => {
        const requirements = solarTradelineItemRequirements(item, storageOnlyProject)
        return {
          ...item,
          loanAgreementDocs: requirements.needsAgreement
            ? await uploadInquiryDocs(item.loanAgreementDocs, SOLAR_TRADELINE_LOAN_AGREEMENT_DOC_TYPE)
            : [],
          payoffLetterDocs: requirements.needsPayoff
            ? await uploadInquiryDocs(item.payoffLetterDocs, SOLAR_TRADELINE_PAYOFF_LETTER_DOC_TYPE)
            : [],
        }
      }))

      await save({
        data: {
          ...formData,
          solarTradelineInquiryItems: uploadedItems,
        },
        advance: false,
        onCatch(ex: unknown) {
          onError(errorToString(ex))
        },
      })
    } catch (ex: unknown) {
      onError(errorToString(ex))
    } finally {
      setSubmitting(false)
    }
  }

  if (!items.length) {
    return null
  }

  return (
    <div className="solar-tradeline-inquiry-hold">
      <h2>Potential Solar-Related Accounts</h2>
      <p className="mt-0">
        We found one or more accounts on the credit report that may be related to solar financing. 
        Please review each account and answer a few questions so we can better understand them.
      </p>
      {visibleItems.map(item => (
        <SolarTradelineInquiryItemForm
          key={item.id}
          item={item}
          formData={formData}
          storageOnlyProject={storageOnlyProject}
          onChange={patch => updateItem(item.id, patch)}
        />
      ))}
      {canSubmit ? (
        <div className="buttons mb-3">
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || submitting}
            onClick={handleContinue}
          >
            {saving || submitting ? <Spinner /> : 'Continue'}
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default SolarTradelineInquiryHold
